/**
 * Centralized Firestore path builders for the multi-game platform.
 *
 * Inspired by Orchestrate's `paths.ts` pattern: every Firestore document/
 * collection reference in the codebase MUST be constructed via a builder
 * defined here. Never hardcode path strings or segment names anywhere else
 * in the app.
 *
 * Naming convention:
 * - `dbc*` builders return a path to a *collection* (odd number of segments).
 * - `dbd*` builders return a path to a *document*  (even number of segments).
 *
 * Each builder returns the path as a string array so that callers can spread
 * it directly into Firestore's modular API:
 *   doc( db, ...dbdRoom( code ) )
 *   collection( db, ...dbcRooms() )
 */

// --- Segment constants ----------------------------------------------------

/**
 * Top-level container under the existing `john-morrell` Firebase project's
 * `/games/{doc}/{subcollection}/**` permissive rule. Everything for the games
 * hub lives under `/games/hub/...` so we don't need new firestore.rules.
 */
export const GAMES_ROOT = 'games'
export const HUB_DOC = 'hub'

/** Rooms subcollection under the hub doc. */
export const ROOMS = 'rooms'

/** Subcollection under a room that holds the game-specific state document. */
export const STATE = 'state'

/** Subcollection under a room that holds private per-role documents. */
export const PRIVATE = 'private'

/** Fixed document id for the singleton "main" game-state document. */
export const STATE_DOC_ID = 'main'

// --- Path builders --------------------------------------------------------

/** Collection of all rooms across all games. */
export const dbcRooms = () => [ GAMES_ROOT, HUB_DOC, ROOMS ] as const

/** A specific room document (lobby + platform-level state). */
export const dbdRoom = ( code: string ) => [ GAMES_ROOT, HUB_DOC, ROOMS, code ] as const

/** Subcollection of state documents under a room (currently just `main`). */
export const dbcRoomState = ( code: string ) => [ GAMES_ROOT, HUB_DOC, ROOMS, code, STATE ] as const

/** The game-specific state document for a room (subcollection-of-one pattern). */
export const dbdRoomState = ( code: string ) => [ GAMES_ROOT, HUB_DOC, ROOMS, code, STATE, STATE_DOC_ID ] as const

/** Subcollection of private documents under a room (e.g. Mr. X position). */
export const dbcRoomPrivate = ( code: string ) => [ GAMES_ROOT, HUB_DOC, ROOMS, code, PRIVATE ] as const

/** A single private subdoc under a room, keyed by `key` (e.g. `'mrx'`). */
export const dbdRoomPrivate = ( code: string, key: string ) => [ GAMES_ROOT, HUB_DOC, ROOMS, code, PRIVATE, key ] as const
